"use client"

import { useState, useEffect } from "react"
import { DashboardLayout } from "@/components/dashboard/dashboard-layout"
import { useNavigate } from "react-router-dom"
import { useAuth } from "@/src/contexts/AuthContext"
import { businessesAPI } from "@/src/lib/api/businesses"
import { getBusinessId } from "@/lib/api/axios-instance"
import { toast } from "sonner"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Textarea } from "@/components/ui/textarea"
import { Label } from "@/components/ui/label"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { ArrowLeft, Loader2, Store, User, Building2, ShieldAlert } from "lucide-react"
import { useThaiLocationForm } from "@/hooks/useThaiLocationForm"
import { ThaiLocationSelect } from "@/components/forms/ThaiLocationSelect"
import type { Address } from "@/src/lib/api/businesses"

const BUSINESS_TYPES = [
  { value: "retail", label: "ค้าปลีก" },
  { value: "restaurant", label: "ร้านอาหาร" },
  { value: "service", label: "บริการ" },
  { value: "manufacturing", label: "การผลิต" },
  { value: "wholesale", label: "ค้าส่ง" },
  { value: "online", label: "ออนไลน์" },
  { value: "other", label: "อื่นๆ" },
]

interface FormData {
  business_name: string
  business_type: string
  tax_id: string
  business_description: string
  contact_phone: string
  contact_email: string
  address: Address
  website: string
}

interface FormErrors {
  business_name?: string
  business_type?: string
  tax_id?: string
  contact_phone?: string
  contact_email?: string
  website?: string
  postal_code?: string
}

export default function EditStorePage() {
  const navigate = useNavigate()
  const { user, refreshUser } = useAuth()
  const [isLoading, setIsLoading] = useState(true)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [errors, setErrors] = useState<FormErrors>({})
  const [prefillStage, setPrefillStage] = useState<"idle" | "province-set" | "done">("done")
  const [prefillLocation, setPrefillLocation] = useState<{
    province: string
    amphure: string
    district: string
    postalCode: string
  } | null>(null)

  const [formData, setFormData] = useState<FormData>({
    business_name: "",
    business_type: "",
    tax_id: "",
    business_description: "",
    contact_phone: "",
    contact_email: "",
    address: { street: "", city: "", district: "", province: "", postal_code: "" },
    website: "",
  })

  const {
    formData: locationData,
    loading: locationLoading,
    provinces,
    amphures,
    districts,
    handleProvinceChange,
    handleAmphureChange,
    handleDistrictChange,
  } = useThaiLocationForm({
    onError: (error) => {
      console.error("Location API error:", error)
      toast.error("ไม่สามารถโหลดข้อมูลที่อยู่ได้ กรุณาลองใหม่อีกครั้ง")
    },
  })

  // Load business data
  useEffect(() => {
    const loadBusiness = async () => {
      try {
        setIsLoading(true)
        setLoadError(null)
        const businessId = getBusinessId()
        if (!businessId) {
          throw new Error("ไม่พบข้อมูลร้านค้า กรุณาเข้าสู่ระบบใหม่")
        }
        const business = await businessesAPI.getById(businessId)

        // Parse address - could be object, JSON string, or plain string
        let addr: Address = { street: "", city: "", district: "", province: "", postal_code: "" }
        if (business.address && typeof business.address === "object") {
          const address = business.address as Record<string, any>
          addr = {
            street: address.street || address.address || "",
            city: address.city || address.subDistrict || address.amphure || "",
            district: address.district || address.tambon || "",
            province: address.province || "",
            postal_code: address.postal_code || address.postalCode || address.zipcode || "",
          }
        } else if (typeof business.address === "string") {
          // Try to parse as JSON first (backend may store address as JSON string)
          try {
            const parsed = JSON.parse(business.address)
            if (parsed && typeof parsed === "object") {
              addr = {
                street: parsed.street || "",
                city: parsed.city || parsed.subDistrict || parsed.amphure || "",
                district: parsed.district || parsed.tambon || "",
                province: parsed.province || "",
                postal_code: parsed.postal_code || parsed.postalCode || parsed.zipcode || "",
              }
            } else {
              addr.street = business.address
            }
          } catch {
            // Not JSON, treat as plain address string
            addr.street = business.address
          }
        }

        setFormData({
          business_name: business.business_name || "",
          business_type: business.business_type || "",
          tax_id: business.tax_id || "",
          business_description: business.business_description || business.description || "",
          contact_phone: business.contact_phone || business.phone || "",
          contact_email: business.contact_email || business.email || "",
          address: addr,
          website: business.website || "",
        })

        setPrefillLocation({
          province: addr.province || "",
          amphure: addr.city || "",
          district: addr.district || "",
          postalCode: addr.postal_code || "",
        })
        setPrefillStage("idle")
      } catch (err: any) {
        console.error("Failed to load business data:", err)
        setLoadError(err.message || "ไม่สามารถโหลดข้อมูลร้านค้าได้")
      } finally {
        setIsLoading(false)
      }
    }
    loadBusiness()
  }, [])

  const normalizeLocationName = (value: string) => value.trim().toLowerCase().replace(/^จังหวัด\s*/, "")

  useEffect(() => {
    if (prefillStage !== "idle" || !prefillLocation?.province || provinces.length === 0) {
      return
    }

    const matchedProvince = provinces.find(
      province => normalizeLocationName(province.name_th) === normalizeLocationName(prefillLocation.province)
    )

    if (!matchedProvince) {
      setPrefillStage("done")
      return
    }

    handleProvinceChange(matchedProvince.name_th, matchedProvince.id)
    updateAddress("province", matchedProvince.name_th)
    setPrefillStage("province-set")
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefillStage, prefillLocation, provinces])

  useEffect(() => {
    if (prefillStage !== "province-set" || !prefillLocation || amphures.length === 0) {
      return
    }

    const matchedAmphure = amphures.find(
      amphure => normalizeLocationName(amphure.name_th) === normalizeLocationName(prefillLocation.amphure)
    )

    if (matchedAmphure) {
      handleAmphureChange(matchedAmphure.name_th, matchedAmphure.id)
      updateAddress("city", matchedAmphure.name_th)
    }

    if (prefillLocation.postalCode) {
      updateAddress("postal_code", prefillLocation.postalCode)
    }

    setPrefillStage("done")
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefillStage, prefillLocation, amphures])

  useEffect(() => {
    if (!locationData.postalCode) {
      return
    }
    updateAddress("postal_code", locationData.postalCode)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locationData.postalCode])

  // Update single field
  const updateField = (field: keyof FormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }))
    // Clear error for this field
    if (errors[field as keyof FormErrors]) {
      setErrors(prev => ({ ...prev, [field]: undefined }))
    }
  }

  // Update address sub-field
  const updateAddress = (field: keyof Address, value: string) => {
    setFormData(prev => ({
      ...prev,
      address: { ...prev.address, [field]: value },
    }))
    if (field === "postal_code" && errors.postal_code) {
      setErrors(prev => ({ ...prev, postal_code: undefined }))
    }
  }

  const onProvinceSelect = (value: string, id: number) => {
    handleProvinceChange(value, id)
    updateAddress("province", value)
    updateAddress("city", "")
    updateAddress("district", "")
    updateAddress("postal_code", "")
  }

  const onAmphureSelect = (value: string, id: number) => {
    handleAmphureChange(value, id)
    updateAddress("city", value)
    updateAddress("district", "")
    updateAddress("postal_code", "")
  }

  const onDistrictSelect = (value: string, id: number) => {
    handleDistrictChange(value, id)
    updateAddress("district", value)
  }

  // Validate form
  const validate = (): boolean => {
    const newErrors: FormErrors = {}

    if (!formData.business_name.trim()) {
      newErrors.business_name = "กรุณากรอกชื่อร้านค้า"
    } else if (formData.business_name.trim().length < 2) {
      newErrors.business_name = "ชื่อร้านค้าต้องมีอย่างน้อย 2 ตัวอักษร"
    }

    if (!formData.business_type) {
      newErrors.business_type = "กรุณาเลือกประเภทธุรกิจ"
    }

    if (formData.tax_id && !/^\d{13}$/.test(formData.tax_id.replace(/[-\s]/g, ""))) {
      newErrors.tax_id = "เลขผู้เสียภาษีต้องเป็นตัวเลข 13 หลัก"
    }

    if (formData.contact_phone && !/^0[6-9]\d{8}$/.test(formData.contact_phone.replace(/[-\s]/g, ""))) {
      newErrors.contact_phone = "เบอร์โทรไม่ถูกต้อง (เช่น 0812345678)"
    }

    if (formData.contact_email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.contact_email)) {
      newErrors.contact_email = "รูปแบบอีเมลไม่ถูกต้อง"
    }

    if (formData.website && !/^https?:\/\/.+\..+/.test(formData.website)) {
      newErrors.website = "รูปแบบ URL ไม่ถูกต้อง (ต้องขึ้นต้นด้วย http:// หรือ https://)"
    }

    if (formData.address.postal_code && !/^\d{5}$/.test(formData.address.postal_code)) {
      newErrors.postal_code = "รหัสไปรษณีย์ต้องเป็นตัวเลข 5 หลัก"
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  // Submit
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!validate()) return

    try {
      setIsSubmitting(true)
      const businessId = getBusinessId()
      if (!businessId) {
        throw new Error("ไม่พบข้อมูลร้านค้า กรุณาเข้าสู่ระบบใหม่")
      }

      // Build update payload - only send fields that have values
      const payload: Record<string, any> = {}

      if (formData.business_name.trim()) {
        payload.business_name = formData.business_name.trim()
      }
      if (formData.business_type) {
        payload.business_type = formData.business_type
      }
      payload.tax_id = formData.tax_id.replace(/[-\s]/g, "") || null
      payload.business_description = formData.business_description.trim() || null
      payload.contact_phone = formData.contact_phone.replace(/[-\s]/g, "") || null
      payload.contact_email = formData.contact_email.trim() || null
      payload.website = formData.website.trim() || null

      // Only send address if at least one field is filled
      const addr = formData.address
      const hasAddress = addr.street || addr.city || addr.district || addr.province || addr.postal_code
      if (hasAddress) {
        payload.address = JSON.stringify({
          street: addr.street?.trim() || "",
          city: addr.city?.trim() || "",
          district: addr.district?.trim() || "",
          province: addr.province?.trim() || "",
          postal_code: addr.postal_code?.trim() || "",
        })
      }

      await businessesAPI.update(businessId, payload)

      // Update local user data so dashboard + sidebar reflect changes immediately
      if (formData.business_name.trim()) {
        const storedUser = localStorage.getItem('user')
        if (storedUser) {
          const userData = JSON.parse(storedUser)
          userData.shopName = formData.business_name.trim()
          localStorage.setItem('user', JSON.stringify(userData))
        }
      }
      await refreshUser()

      toast.success("บันทึกข้อมูลร้านค้าเรียบร้อยแล้ว", {
        description: "ข้อมูลร้านค้าได้รับการอัปเดตแล้ว",
      })

      // Navigate back to dashboard
      navigate("/dashboard")
    } catch (err: any) {
      console.error("Failed to update business:", err)
      const message = err.response?.data?.error || err.response?.data?.message || err.message || "เกิดข้อผิดพลาดในการบันทึก"
      toast.error(message)
    } finally {
      setIsSubmitting(false)
    }
  }

  // Loading state
  if (isLoading) {
    return (
      <DashboardLayout>
        <div className="w-full flex flex-col items-center justify-center min-h-[60vh]">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          <p className="mt-4 text-muted-foreground">กำลังโหลดข้อมูลร้านค้า...</p>
        </div>
      </DashboardLayout>
    )
  }

  // Error state
  if (loadError) {
    return (
      <DashboardLayout>
        <div className="w-full flex flex-col items-center justify-center min-h-[60vh]">
          <ShieldAlert className="h-12 w-12 text-red-500 mb-4" />
          <h2 className="text-lg font-semibold text-gray-900 mb-2">ไม่สามารถโหลดข้อมูลได้</h2>
          <p className="text-muted-foreground mb-4">{loadError}</p>
          <Button variant="outline" onClick={() => navigate("/dashboard")}>
            กลับหน้าแดชบอร์ด
          </Button>
        </div>
      </DashboardLayout>
    )
  }

  return (
    <DashboardLayout>
      <form onSubmit={handleSubmit} className="w-full space-y-6">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button type="button" variant="ghost" size="icon" onClick={() => navigate(-1)} className="h-9 w-9">
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">แก้ไขข้อมูลร้านค้า</h1>
            <p className="text-sm text-muted-foreground">จัดการข้อมูลทั่วไปของร้านค้าคุณ</p>
          </div>
        </div>

        {/* Section 1: Account Info (Readonly) */}
        <Card>
          <CardHeader className="pb-4">
            <div className="flex items-center gap-2">
              <User className="h-5 w-5 text-muted-foreground" />
              <CardTitle className="text-lg">ข้อมูลบัญชีผู้ใช้</CardTitle>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="username">Username</Label>
              <Input
                id="username"
                value={user?.email || ""}
                disabled
                className="bg-muted text-muted-foreground"
              />
              <p className="text-xs text-muted-foreground">Username ที่ใช้เข้าสู่ระบบ (ไม่สามารถแก้ไขได้)</p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="email">อีเมล</Label>
              <Input
                id="email"
                value={user?.email || ""}
                readOnly
                className="bg-muted text-muted-foreground"
              />
              <p className="text-xs text-muted-foreground">อีเมลที่ใช้เข้าสู่ระบบ (ไม่สามารถแก้ไขได้)</p>
            </div>
          </CardContent>
        </Card>

        {/* Section 2: Business Info (Editable) */}
        <Card>
          <CardHeader className="pb-4">
            <div className="flex items-center gap-2">
              <Building2 className="h-5 w-5 text-muted-foreground" />
              <CardTitle className="text-lg">ข้อมูลทั่วไปร้านค้า</CardTitle>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Business Name */}
            <div className="space-y-2">
              <Label htmlFor="business_name">
                ชื่อร้านค้า <span className="text-red-500">*</span>
              </Label>
              <Input
                id="business_name"
                placeholder="กรอกชื่อร้านค้า"
                value={formData.business_name}
                onChange={e => updateField("business_name", e.target.value)}
                className={errors.business_name ? "border-red-500" : ""}
                disabled={isSubmitting}
              />
              {errors.business_name && (
                <p className="text-sm text-red-500">{errors.business_name}</p>
              )}
            </div>

            {/* Business Type */}
            <div className="space-y-2">
              <Label htmlFor="business_type">
                ประเภทธุรกิจ <span className="text-red-500">*</span>
              </Label>
              <Select
                value={formData.business_type}
                onValueChange={value => updateField("business_type", value)}
                disabled={isSubmitting}
              >
                <SelectTrigger id="business_type" className={errors.business_type ? "border-red-500" : ""}>
                  <SelectValue placeholder="เลือกประเภทธุรกิจ" />
                </SelectTrigger>
                <SelectContent className="bg-white">
                  {BUSINESS_TYPES.map(type => (
                    <SelectItem key={type.value} value={type.value}>
                      {type.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {errors.business_type && (
                <p className="text-sm text-red-500">{errors.business_type}</p>
              )}
            </div>

            {/* Tax ID */}
            <div className="space-y-2">
              <Label htmlFor="tax_id">เลขประจำตัวผู้เสียภาษี</Label>
              <Input
                id="tax_id"
                placeholder="กรอกเลขผู้เสียภาษี 13 หลัก"
                value={formData.tax_id}
                onChange={e => updateField("tax_id", e.target.value.replace(/[^0-9]/g, ""))}
                maxLength={13}
                className={errors.tax_id ? "border-red-500" : ""}
                disabled={isSubmitting}
              />
              {errors.tax_id && (
                <p className="text-sm text-red-500">{errors.tax_id}</p>
              )}
            </div>

            {/* Description */}
            <div className="space-y-2">
              <Label htmlFor="business_description">คำอธิบายธุรกิจ</Label>
              <Textarea
                id="business_description"
                placeholder="อธิบายเกี่ยวกับธุรกิจของคุณ (ไม่เกิน 500 ตัวอักษร)"
                value={formData.business_description}
                onChange={e => updateField("business_description", e.target.value)}
                rows={3}
                maxLength={500}
                disabled={isSubmitting}
                className="resize-none"
              />
              <div className="flex justify-end">
                <span className="text-xs text-muted-foreground">
                  {formData.business_description.length}/500
                </span>
              </div>
            </div>

            {/* Contact Phone */}
            <div className="space-y-2">
              <Label htmlFor="contact_phone">เบอร์โทรติดต่อ</Label>
              <Input
                id="contact_phone"
                type="tel"
                placeholder="0812345678"
                value={formData.contact_phone}
                onChange={e => updateField("contact_phone", e.target.value.replace(/[^0-9]/g, ""))}
                maxLength={10}
                className={errors.contact_phone ? "border-red-500" : ""}
                disabled={isSubmitting}
              />
              {errors.contact_phone && (
                <p className="text-sm text-red-500">{errors.contact_phone}</p>
              )}
            </div>

            {/* Contact Email */}
            <div className="space-y-2">
              <Label htmlFor="contact_email">อีเมลติดต่อ</Label>
              <Input
                id="contact_email"
                type="email"
                placeholder="contact@example.com"
                value={formData.contact_email}
                onChange={e => updateField("contact_email", e.target.value)}
                className={errors.contact_email ? "border-red-500" : ""}
                disabled={isSubmitting}
              />
              {errors.contact_email && (
                <p className="text-sm text-red-500">{errors.contact_email}</p>
              )}
            </div>

            {/* Website */}
            <div className="space-y-2">
              <Label htmlFor="website">เว็บไซต์</Label>
              <Input
                id="website"
                type="url"
                placeholder="https://www.example.com"
                value={formData.website}
                onChange={e => updateField("website", e.target.value)}
                className={errors.website ? "border-red-500" : ""}
                disabled={isSubmitting}
              />
              {errors.website && (
                <p className="text-sm text-red-500">{errors.website}</p>
              )}
            </div>

            {/* Address Section */}
            <div className="pt-2">
              <Label className="mb-3 block font-medium text-sm">
                <Store className="h-4 w-4 inline mr-1.5" />
                ที่อยู่
              </Label>
              <div className="space-y-3 pl-1">
                <Input
                  placeholder="ที่อยู่ (บ้านเลขที่, ถนน, ซอย)"
                  value={formData.address.street || ""}
                  onChange={e => updateAddress("street", e.target.value)}
                  disabled={isSubmitting}
                />
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <ThaiLocationSelect
                    label="จังหวัด"
                    value={locationData.province || formData.address.province || ""}
                    onChange={onProvinceSelect}
                    options={provinces}
                    placeholder="เลือกจังหวัด"
                    loading={locationLoading.provinces}
                    disabled={isSubmitting || locationLoading.provinces}
                  />
                  <ThaiLocationSelect
                    label="เขต / อำเภอ"
                    value={locationData.amphure || formData.address.city || ""}
                    onChange={onAmphureSelect}
                    options={amphures}
                    placeholder={locationData.provinceId ? "เลือกเขต / อำเภอ" : "เลือกจังหวัดก่อน"}
                    loading={locationLoading.amphures}
                    disabled={isSubmitting || !locationData.provinceId || locationLoading.amphures}
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <ThaiLocationSelect
                    label="แขวง / ตำบล"
                    value={locationData.tambon || formData.address.district || ""}
                    onChange={onDistrictSelect}
                    options={districts}
                    placeholder={locationData.amphureId ? "เลือกแขวง / ตำบล" : "เลือกเขต / อำเภอก่อน"}
                    loading={locationLoading.districts}
                    disabled={isSubmitting || !locationData.amphureId || locationLoading.districts}
                  />
                  <div className="space-y-2">
                    <Label className="text-[#323130] font-medium">รหัสไปรษณีย์</Label>
                    <Input
                      placeholder="อัตโนมัติเมื่อเลือกตำบล"
                      value={locationData.postalCode || formData.address.postal_code || ""}
                      readOnly
                      className={`bg-gray-50 ${errors.postal_code ? "border-red-500" : ""}`}
                      disabled
                    />
                  </div>
                </div>
                {errors.postal_code && (
                  <p className="text-sm text-red-500">{errors.postal_code}</p>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Action Buttons */}
        <div className="flex items-center justify-end gap-3 pt-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate(-1)}
            disabled={isSubmitting}
          >
            ยกเลิก
          </Button>
          <Button type="submit" disabled={isSubmitting}>
            {isSubmitting ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                กำลังบันทึก...
              </>
            ) : (
              "บันทึก"
            )}
          </Button>
        </div>
      </form>
    </DashboardLayout>
  )
}
